import React, { useMemo, useState } from 'react'
import {
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Typography, Chip, Box, TextField, MenuItem
} from '@material-ui/core'

const statusColor = {
  covered: '#4caf50',
  partial: '#ff9800',
  missing: '#f44336',
  needs_review: '#9e9e9e'
}

const ServiceTable = ({ serviceSummary, services, selectedServiceId, onSelectService }) => {
  const [categoryFilter, setCategoryFilter] = useState('All')

  // Build a lookup for service metadata
  const serviceMetaMap = useMemo(() => {
    const map = {}
    for (const svc of services) {
      map[svc.serviceId] = svc
    }
    return map
  }, [services])

  const categories = useMemo(() => {
    const cats = new Set()
    serviceSummary.forEach(s => {
      const meta = serviceMetaMap[s.serviceId]
      if (meta) cats.add(meta.category)
    })
    return ['All', ...Array.from(cats).sort()]
  }, [serviceSummary, serviceMetaMap])

  const filtered = useMemo(() => {
    if (categoryFilter === 'All') return serviceSummary
    return serviceSummary.filter(s => {
      const meta = serviceMetaMap[s.serviceId]
      return meta && meta.category === categoryFilter
    })
  }, [serviceSummary, categoryFilter, serviceMetaMap])

  const fmt = (n) => (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

  return (
    <Box>
      <Box p={1.5} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Services ({filtered.length})
        </Typography>
        <TextField
          select
          size="small"
          variant="outlined"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{ width: 160 }}
        >
          {categories.map(c => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </TextField>
      </Box>
      <TableContainer style={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Service</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell>Items</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(svc => {
              const meta = serviceMetaMap[svc.serviceId] || {}
              const worstStatus = svc.lineItems.reduce((worst, li) => {
                const priority = { missing: 3, needs_review: 2, partial: 1, covered: 0 }
                return (priority[li.coverageStatus] || 0) > (priority[worst] || 0)
                  ? li.coverageStatus : worst
              }, 'covered')

              return (
                <TableRow
                  key={svc.serviceId}
                  hover
                  selected={svc.serviceId === selectedServiceId}
                  onClick={() => onSelectService(svc.serviceId)}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {meta.name || svc.serviceId}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {meta.category || ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{svc.totalQty}</TableCell>
                  <TableCell>{svc.lineItems.length} items</TableCell>
                  <TableCell align="right">{fmt(svc.totalCost)}</TableCell>
                  <TableCell>
                    <Chip
                      label={worstStatus.replace('_', ' ')}
                      size="small"
                      style={{
                        backgroundColor: statusColor[worstStatus] || '#9e9e9e',
                        color: '#fff',
                        fontSize: 11,
                        height: 22
                      }}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="textSecondary" variant="body2" style={{ padding: 16 }}>
                    No services assigned yet. Assign services from the Estimate tab.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default ServiceTable
